/**
 * Simple pluralization helper
 * @param count - The number to check for pluralization
 * @param singular - The singular form of the word
 * @param plural - The plural form of the word (defaults to singular + 's')
 * @returns The appropriate singular or plural form
 * @example
 * pluralize(1, 'item') // → 'item'
 * pluralize(2, 'item') // → 'items'
 * pluralize(2, 'child', 'children') // → 'children'
 */
export const pluralize = (
  count: number,
  singular: string,
  plural?: string,
): string => {
  // Handle invalid numbers
  if (!Number.isFinite(count)) {
    // eslint-disable-next-line no-console
    console.warn(`pluralize: Invalid count provided: ${count}`);
    return plural || `${singular}s`;
  }

  // Use absolute value and round for edge cases
  const absCount = Math.abs(Math.round(count));
  return absCount === 1 ? singular : plural || `${singular}s`;
};

/**
 * Formats a count with the appropriate plural form
 * @param count - The number to include
 * @param singular - The singular form of the word
 * @param plural - The plural form of the word (defaults to singular + 's')
 * @param template - Optional template string where {{count}} is replaced with the formatted text
 * @returns The formatted string with count and appropriate plural form
 * @example
 * formatCount(3, 'item') // → '3 items'
 * formatCount(1, 'item') // → '1 item'
 * formatCount(2, 'child', 'children') // → '2 children'
 * formatCount(3, 'item', undefined, '({{count}})') // → '(3 items)'
 */
export const formatCount = (
  count: number,
  singular: string,
  plural?: string,
  template?: string,
): string => {
  // Handle invalid numbers - display 0 for safety
  const safeCount = Number.isFinite(count) ? Math.round(count) : 0;
  const text = `${safeCount} ${pluralize(safeCount, singular, plural)}`;

  if (template) {
    return template.replace('{{count}}', text);
  }

  return text;
};
